import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import {
  Box,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@material-ui/icons";

import { TypingIndicator } from "components";
import {
  createListingNote,
  updateListingNote,
  useListingNote,
} from "services/listingNotes";
import { Theme } from "theme";

const MAX_NOTE_LENGTH = 1000;
const AUTO_SAVE_DELAY = 2000;
const TYPING_INDICATOR_TIMEOUT = 2000;
const HELPER_TEXT_TIMEOUT = 3000;
const SPECIAL_CHARS_REGEX = /[^A-Za-z0-9À-ÖØ-öø-ÿ\s,\.!\?:;\-'""]/;
const ERROR_MESSAGES = {
  specialChars: "Special characters are not allowed",
  saveFailed: "Error saving listing note",
};

const useStyles = makeStyles<Theme, { showHelperText: boolean }>(theme => ({
  container: { width: "100%", display: "flex", flexDirection: "column" },
  textField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.text.primary,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-multiline": {
      padding: 0,
    },
    "& .MuiOutlinedInput-inputMultiline": {
      padding: theme.spacing(1),
    },
    "& .MuiFormHelperText-root": {
      marginLeft: theme.spacing(0),
    },
  },
  savedCheckIcon: {
    fontSize: "0.9rem",
    color: theme.palette.success.main,
    marginLeft: theme.spacing(0.5),
    verticalAlign: "middle",
  },
  sectionTitle: {
    fontSize: "1.5rem",
    fontWeight: theme.typography.fontWeightBold,
    paddingRight: theme.spacing(0.5),
    textTransform: "none",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  infoIcon: {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
    cursor: "help",
  },
  helperTextContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    minHeight: "1.5rem",
    transition: "opacity 0.2s ease-in-out",
    opacity: ({ showHelperText }) => (showHelperText ? 1 : 0),
  },
  savedCheckText: {
    fontSize: "0.9em",
    color: theme.palette.success.main,
    marginLeft: theme.spacing(0.25),
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(0.25),
  },
  errorIcon: {
    fontSize: "0.9rem",
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
    verticalAlign: "middle",
  },
  errorText: {
    fontSize: "0.9em",
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.25),
  },
  noteLength: {
    marginRight: theme.spacing(0.8),
  },
}));

interface ListingNoteProps {
  listingId: number;
  isSecondaryMarketOwner: boolean;
}

const ListingNote: FC<ListingNoteProps> = ({
  listingId,
  isSecondaryMarketOwner,
}) => {
  const [noteContent, setNoteContent] = useState("");
  const [showTypingIndicator, setShowTypingIndicator] = useState(true);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isFocused, setIsFocused] = useState(true);
  const [showHelperText, setShowHelperText] = useState(true);
  const [error, setError] = useState(null);

  const classes = useStyles({ showHelperText: showHelperText });

  const { data: existingNote, refetch } = useListingNote(listingId);

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedContent = event.target.value;
    if (updatedContent.length > MAX_NOTE_LENGTH) {
      return;
    }

    if (SPECIAL_CHARS_REGEX.test(updatedContent)) {
      setError(ERROR_MESSAGES.specialChars);
    } else {
      setError(null);
    }

    setNoteContent(updatedContent);
    setShowSaveConfirmation(false);
    setShowTypingIndicator(true);
  };

  const saveNote = useCallback(async () => {
    if (!noteContent || error) {
      return;
    }
    if (noteContent === existingNote?.content) {
      return;
    }

    try {
      if (existingNote) {
        await updateListingNote(existingNote.id, noteContent);
      } else {
        await createListingNote(listingId, noteContent);
      }
      await refetch();
      setShowSaveConfirmation(true);
    } catch (error) {
      setError(ERROR_MESSAGES.saveFailed);
    }
  }, [noteContent, existingNote, listingId, refetch, error]);

  useEffect(() => {
    if (existingNote) {
      setNoteContent(existingNote.content);
    }
  }, [existingNote]);

  useEffect(() => {
    const listingNoteTimeoutId = setTimeout(() => {
      saveNote();
    }, AUTO_SAVE_DELAY);

    return () => clearTimeout(listingNoteTimeoutId);
  }, [saveNote]);

  useEffect(() => {
    const typingIndicatorTimeoutId = setTimeout(
      () => setShowTypingIndicator(false),
      TYPING_INDICATOR_TIMEOUT,
    );

    return () => clearTimeout(typingIndicatorTimeoutId);
  }, [showTypingIndicator]);

  useEffect(() => {
    if (isFocused) {
      setShowHelperText(true);
      return;
    }

    const hideHelperTimeout = setTimeout(() => {
      setShowHelperText(false);
      setShowSaveConfirmation(false);
    }, HELPER_TEXT_TIMEOUT);

    return () => clearTimeout(hideHelperTimeout);
  }, [isFocused]);

  const helperText = (
    <Box className={classes.helperTextContainer}>
      <span className={classes.noteLength}>
        {`${noteContent.length}/${MAX_NOTE_LENGTH}`}
      </span>
      {showTypingIndicator ? (
        <TypingIndicator />
      ) : showSaveConfirmation && noteContent ? (
        <Box className={classes.iconContainer}>
          <CheckIcon className={classes.savedCheckIcon} />
          <Typography variant="body2" className={classes.savedCheckText}>
            Saved
          </Typography>
        </Box>
      ) : error ? (
        <Box className={classes.iconContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box className={classes.container} mb={0}>
      <Box className={classes.headerRow} mb={1}>
        <Typography variant="h3" className={classes.sectionTitle}>
          Listing Notes
        </Typography>
        <Tooltip
          title={
            isSecondaryMarketOwner
              ? "Listing notes are private and visible only to you. They will never be shared with buyers."
              : "Listing notes are private and visible only to you. They will never be shared with sellers or other buyers."
          }
          placement="right"
        >
          <InfoIcon className={classes.infoIcon} />
        </Tooltip>
      </Box>
      <TextField
        className={classes.textField}
        placeholder="Start a private note on this listing"
        multiline
        rows={1}
        rowsMax={10}
        variant="outlined"
        value={noteContent}
        onChange={handleNoteChange}
        autoFocus={noteContent.length > 0}
        inputProps={{ maxLength: MAX_NOTE_LENGTH }}
        helperText={helperText}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          saveNote();
        }}
      />
    </Box>
  );
};

export default ListingNote;
